/**
 * Utility functions for formatting WhatsApp Business templates for preview
 */

// Media file paths from public/files directory
const MEDIA_FILES = {
    IMAGE: '/files/image1.jpg',
    VIDEO: '/files/video.mp4',
    DOCUMENT: '/files/dummy.pdf',
};

/**
 * Extracts parameter numbers from text (e.g., {{1}}, {{2}})
 * @param {string} text - The text containing parameters
 * @returns {Array<number>} - Array of parameter numbers
 */
export const extractParameters = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    const paramNumbers = matches.map(match => parseInt(match.replace(/\{\{|\}\}/g, '')));
    return [...new Set(paramNumbers)].sort((a, b) => a - b);
};

/**
 * Replaces parameters in text with sample values
 * @param {string} text - The text containing parameters like {{1}}, {{2}}
 * @param {Array<string>} sampleValues - Array of sample values to replace parameters
 * @returns {string} - Text with parameters replaced
 */
export const replaceParameters = (text, sampleValues = []) => {
    if (!text) return '';
    let result = text;
    
    // Extract all parameter numbers
    const paramNumbers = extractParameters(text);
    
    // Replace each parameter with sample value or keep the placeholder
    paramNumbers.forEach((paramNum, index) => {
        const sampleValue = sampleValues[index] || `Sample Value ${paramNum}`;
        result = result.replace(
            new RegExp(`\\{\\{${paramNum}\\}\\}`, 'g'),
            sampleValue
        );
    });
    
    return result;
};

/**
 * Formats template details for preview
 * @param {Object} templateData - Template data from API
 * @returns {Object} - Formatted template data for preview
 */
export const formatTemplateForPreview = (templateData) => {
    if (!templateData || !templateData.components) {
        return null;
    }

    const formatted = {
        name: templateData.name || '',
        language: templateData.language || '',
        category: templateData.category || '',
        status: templateData.status || '',
        header: null,
        body: null,
        footer: null,
        buttons: null,
        headerMediaPreviewUrl: null,
    };

    // Process header component
    const headerComponent = templateData.components.find(c => c.type === 'HEADER');
    if (headerComponent) {
        if (headerComponent.format === 'TEXT' && headerComponent.text) {
            // Extract parameters and create sample values
            const paramNumbers = extractParameters(headerComponent.text);
            const sampleValues = paramNumbers.map((num, index) => {
                // Check if example values exist
                if (headerComponent.example?.header_text && Array.isArray(headerComponent.example.header_text)) {
                    return headerComponent.example.header_text[index] || `Sample Header ${num}`;
                }
                return `Sample Header ${num}`;
            });
            
            formatted.header = {
                type: 'TEXT',
                text: replaceParameters(headerComponent.text, sampleValues),
                originalText: headerComponent.text,
            };
        } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format)) {
            // Use media from public/files directory
            formatted.header = {
                type: headerComponent.format,
                format: headerComponent.format,
            };
            formatted.headerMediaPreviewUrl = MEDIA_FILES[headerComponent.format] || null;
        }
    }

    // Process body component
    const bodyComponent = templateData.components.find(c => c.type === 'BODY');
    if (bodyComponent && bodyComponent.text) {
        // Extract parameters and create sample values
        const paramNumbers = extractParameters(bodyComponent.text);
        const sampleValues = paramNumbers.map((num, index) => {
            // Check if example values exist (body_text is string[][])
            if (bodyComponent.example?.body_text && 
                Array.isArray(bodyComponent.example.body_text) && 
                bodyComponent.example.body_text[0] && 
                Array.isArray(bodyComponent.example.body_text[0])) {
                return bodyComponent.example.body_text[0][index] || `Sample Body ${num}`;
            }
            return `Sample Body ${num}`;
        });
        
        formatted.body = {
            text: replaceParameters(bodyComponent.text, sampleValues),
            originalText: bodyComponent.text,
        };
    }

    // Process footer component
    const footerComponent = templateData.components.find(c => c.type === 'FOOTER');
    if (footerComponent && footerComponent.text) {
        formatted.footer = {
            text: footerComponent.text,
        };
    }

    // Process buttons component
    const buttonsComponent = templateData.components.find(c => c.type === 'BUTTONS');
    if (buttonsComponent && buttonsComponent.buttons && Array.isArray(buttonsComponent.buttons)) {
        formatted.buttons = buttonsComponent.buttons.map(button => ({
            type: button.type || 'QUICK_REPLY',
            text: button.text || '',
            url: button.url || null,
            phone_number: button.phone_number || null,
        }));
    }

    return formatted;
};

export default {
    extractParameters,
    replaceParameters,
    formatTemplateForPreview,
};

